import { tasksStore, TaskType } from "@/stores/tasks";
import { v4 as uuidv4 } from "uuid";
import { userAssetsStore } from "@/stores/userAssets";
import { CommonMetaData } from "@/types/meta";
import { Editor_Words } from "@/utils/lang/editor";
import { addNewImage, toast } from "@/utils/misc";
import { getRandomInt } from "@/utils/random";
import {
  addVirtualNode,
  postDrawRecord,
  recoverVirtualNode,
  VirtualNodeInfo,
} from "@/utils/safeOp";
import { useToast } from "@chakra-ui/react";
import { BoardStore, translate } from "@noli/business";
import {
  Lang,
  sleep,
  safeCall,
  getRandomSeed,
  Logger,
  shallowCopy,
  getRandomHash,
  getCenteredBBox,
} from "@noli/core";
import { makeAutoObservable } from "mobx";
import { fetchMonkeyTaskStatus } from "../api";
import { requestModel } from "./api";
import { Monkey_Words } from "./lang";
import { formateMeta, FormattedMonkeyMeta, MonkeyMeta } from "./meta";
import { RequestModelParams } from "./types";
import { TaskStatusType, IWithResponse } from "../types";
import { polling } from "@/utils/loopRequestResult";

export interface ModelInfo {
  desc: string;
  image_url: string;
  model_id: string;
  name: string;
  thumb_url: string;
}

export class MonkeyStore {
  modelList: ModelInfo[] = [];
  selectedModel?: ModelInfo;
  prompt = "";
  negativePrompt = "";
  // type: 1 txt2img 2 img2img
  type = 1;
  samplingStep = 20;
  cfgScale = 7;
  imageCfgScale = 0.2;
  width = 512;
  height = 512;
  //批量生成数量 [1, 4]
  batchSize = 1;
  seed = -1;
  url = "";
  // txt2img  img2img
  taskType: "txt2img" | "img2img" = "txt2img";
  runningTaskId = "";

  // component status
  negativeSwitch = false;
  promptExpanding = false;
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get modelId() {
    return this.selectedModel?.model_id;
  }

  setModelList(modelList: ModelInfo[]) {
    this.modelList = modelList;
  }

  setSelectedModel(model?: ModelInfo) {
    this.selectedModel = model;
  }

  setTaskType(taskType: "txt2img" | "img2img") {
    switch (taskType) {
      case "txt2img":
        this.type = 1;
        this.taskType = taskType;
        break;
      case "img2img":
        this.type = 2;
        this.taskType = taskType;
        break;
    }
  }

  setPrompt(prompt: string) {
    this.prompt = prompt;
  }

  setNegativePrompt(prompt: string) {
    this.negativePrompt = prompt;
  }

  setNegativeVisible(visible: boolean) {
    this.negativeSwitch = visible;
  }

  setSamplingStep(samplingStep: number) {
    this.samplingStep = samplingStep;
  }

  setCfgScale(scale: number) {
    this.cfgScale = scale;
  }

  setImageCfgScale(scale: number) {
    this.imageCfgScale = scale;
  }

  setW(w: number) {
    this.width = w;
  }

  setH(h: number) {
    this.height = h;
  }

  setBatchSize(size: number) {
    this.batchSize = size;
  }

  setSeed(seed: number | string) {
    this.seed = Number(seed);
  }

  setUrl(url: string) {
    this.url = url;
  }

  setRunningTask(id: string) {
    this.runningTaskId = id;
  }

  setPromptExpanding(loading: boolean) {
    this.promptExpanding = loading;
  }

  check(): boolean {
    return true;
  }

  buildRequestParams(): RequestModelParams {
    if (!this.modelId) {
      throw new Error("no model id, please select a model");
    }
    const params: RequestModelParams = {
      model_id: this.modelId,
      prompt: this.prompt,
      negative_prompt: this.negativeSwitch ? this.negativePrompt : "",
      type: this.type,
      sampling_step: this.samplingStep,
      cfg_scale: this.cfgScale,
      width: this.width,
      height: this.height,
      batch_size: this.batchSize,
      seed: this.seed === -1 ? Math.round(getRandomSeed() / 2) : this.seed,
      url: this.url,
      task_type: this.taskType,
    };
    if (this.taskType === "img2img") {
      params.image_cfg_scale = Number((1 - this.imageCfgScale).toPrecision(1));
    }
    return params;
  }

  async pollingTask(
    taskId: string,
    {
      t,
      lang,
      meta,
    }: {
      t: ReturnType<typeof useToast>;
      lang: Lang;
      meta: FormattedMonkeyMeta;
    },
  ) {
    // do some thing
    const createTime = Date.now() / 1000;
    const virtualInfo = meta.data.wh;
    const count = meta.data.params.batch_size;
    const virtualNodeInfoList: VirtualNodeInfo[] = [];
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        toast(t, "success", translate(Editor_Words["submit-success-message"], lang));
      }
      virtualNodeInfoList.push({
        alias: await addVirtualNode(t, ".monkey.", lang, virtualInfo),
      });
      await sleep(500);
    }
    const taskType = meta.data.params.task_type;
    const task: TaskType = {
      key: uuidv4(),
      type: `monkey${taskType === "txt2img" ? "" : "-" + taskType}`,
      status: "working",
      pending: 0,
      isKafka: false,
      taskPack: { uri: "", data: {} },
    };
    tasksStore.addTask(task);
    const failed = async (error: any) => {
      for (let i = 0; i < count; i++) {
        await recoverVirtualNode(virtualNodeInfoList[i]);
        await userAssetsStore.fetchHeartCoin();
      }
      if (error === "CANCELLED") {
        return;
      }
      toast(t, "error", translate(Monkey_Words["error-message"], lang));
      tasksStore.removeTask(task);
    };
    // query task
    const res = await polling(
      () => fetchMonkeyTaskStatus(taskId),
      (res) => {
        // 切换项目/退出登录时会把 task 取消
        if (!tasksStore.tasks.get(task.key)) {
          return -2;
        }
        const taskStatus = res.status as TaskStatusType;
        switch (taskStatus) {
          case "exception":
          case "not_found":
            return -1;
          case "pending":
          case "working":
            return 0;
          case "finished":
            return 1;
        }
      },
    ).catch(failed);
    const imgURLs: string[] = res?.data?.img_urls;
    for (let i = 0; i < count; i++) {
      let bbox = await recoverVirtualNode(virtualNodeInfoList[i]);
      if (!bbox) {
        console.error(`===== ${virtualNodeInfoList[i].alias} VirtualNode Error! `);
        bbox = getCenteredBBox(virtualInfo.w, virtualInfo.h, BoardStore.board);
      }
      const url = imgURLs[i];
      if (!url) continue;
      const newAlias = `monkey.${taskId}.${getRandomHash()}`;
      const iMeta = shallowCopy(meta);
      iMeta.data.timestamp = Date.now();
      addNewImage(newAlias, url, {
        info: bbox,
        meta: iMeta,
        callbacks: {
          success: async () => {
            postDrawRecord(url, Date.now() / 1000 - createTime, iMeta);
            if (i === count - 1) {
              toast(t, "success", translate(Editor_Words["generate-success-message"], lang));
            }
          },
          failed: async () => console.error(`Failed to add Monkey at ${i}`),
        },
      });
    }
    if (!res.data.safe) {
      toast(t, "warning", translate(Editor_Words["post-creator-not-safe-warning-message"], lang));
    }
    tasksStore.removeTask(task);
    await userAssetsStore.fetchHeartCoin();
  }

  async postTask({ t, lang }: { t: ReturnType<typeof useToast>; lang: Lang }) {
    const params = this.buildRequestParams();
    const res: IWithResponse<{ id: string }> = await requestModel(params);
    const meta: MonkeyMeta = {
      type: "monkey",
      data: {
        wh: { w: this.width, h: this.height },
        params,
      },
    };
    if (res.code === 0) {
      this.pollingTask(res.data.id, {
        t,
        lang,
        meta: formateMeta(meta),
      });
    }
  }

  static async postMonkeyTask({
    t,
    lang,
    params,
  }: {
    t: ReturnType<typeof useToast>;
    lang: Lang;
    params: RequestModelParams;
  }) {
    const res: IWithResponse<{ id: string }> = await requestModel(params);
    const meta: MonkeyMeta = {
      type: "monkey",
      data: {
        wh: { w: params.width, h: params.height },
        params,
      },
    };
    if (res.code === 0) {
      monkeyStore.pollingTask(res.data.id, {
        t,
        lang,
        meta: formateMeta(meta),
      });
    }
  }
}

export const monkeyStore = new MonkeyStore();
